/**
 * YearOverYearTab
 *
 * Tab 1 orchestrator: connects the YoY controls, KPI cards, and chart
 * to the data hooks. Manages state for professional selection and granularity.
 *
 * Date ranges are fully determined by each attorney's Hourglass go-live date
 * (computed server-side) — no client-side date range selection needed.
 */

import { useState, useMemo, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { YoYGranularity } from "../../types/dashboard";
import {
  useProfessionals,
  useYoYSummary,
  useYoYTimeSeries,
  useGoLiveDates,
  useDailyView,
} from "../../hooks/useAdminData";
import YoYControls from "./YoYControls";
import YoYKpiCards from "./YoYKpiCards";
import YoYChart from "./YoYChart";
import DailyViewChart from "./DailyViewChart";
import loaderStyles from "@/shared/components/LoadingPopup/LoadingPopup.module.css";
import { formatGoLiveDate, toLocalDateStr } from "../../utils/adminHelpers";

function computeDaysBetween(start: string, end: string): number {
  const s = new Date(start + "T00:00:00");
  const e = new Date(end + "T00:00:00");
  return Math.round((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24));
}

export default function YearOverYearTab() {
  const [profId, setProfId] = useState<string | null>(null);
  const [granularity, setGranularity] = useState<YoYGranularity>("weekly");
  const queryClient = useQueryClient();

  const { professionals } = useProfessionals();
  const { goLiveDates } = useGoLiveDates();

  const {
    summary,
    loading: summaryLoading,
    isFetching: summaryFetching,
    isPlaceholderData: summaryIsPlaceholder,
    error: summaryError,
  } = useYoYSummary(profId);

  const {
    data: timeSeriesData,
    loading: _tsLoading,
    isFetching: tsFetching,
    isPlaceholderData: tsIsPlaceholder,
    error: tsError,
  } = useYoYTimeSeries(granularity, profId);

  const {
    data: dailyData,
    loading: dailyLoading,
    isFetching: dailyFetching,
    isPlaceholderData: dailyIsPlaceholder,
    error: dailyError,
  } = useDailyView(granularity === "daily" ? profId : null);

  // Auto-reset to weekly when "All Professionals" is selected while on daily view
  useEffect(() => {
    if (!profId && granularity === "daily") setGranularity("weekly");
  }, [profId, granularity]);

  // Build prof_id → go_live_date map for chart subtitles
  const goLiveDateMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const rec of goLiveDates) {
      map[rec.prof_id] = rec.go_live_date;
    }
    return map;
  }, [goLiveDates]);

  // Hard loading: show full spinner only when summary has no data at all (no real data,
  // no placeholder from a prior key). Granularity switches don't trigger the spinner —
  // the badges stay visible while chart data loads separately.
  const hardLoading = summaryLoading && !summaryIsPlaceholder;

  // Soft loading: dim the content while waiting for data. Includes first-time daily
  // loading (dailyLoading) so granularity switches feel consistent with professional
  // switches — opacity dim rather than spinner in both cases.
  const isShowingPlaceholder =
    summaryIsPlaceholder ||
    (granularity === "daily"
      ? dailyIsPlaceholder || dailyLoading
      : tsIsPlaceholder);

  // Transitioning: any background fetch in progress — drives "Updating…" subtitle
  const transitioning = (summaryFetching || tsFetching || dailyFetching) && !hardLoading;

  // First non-null error to surface in the UI (Sentry capture is handled centrally
  // in QueryCache.onError in queryClient.ts)
  const activeError = summaryError ?? tsError ?? dailyError;

  const handleRetry = () => {
    void queryClient.invalidateQueries({ queryKey: ["yoy-summary"] });
    void queryClient.invalidateQueries({ queryKey: ["yoy-timeseries"] });
    void queryClient.invalidateQueries({ queryKey: ["daily-view"] });
  };

  const goLiveDate = profId ? (goLiveDateMap[profId] ?? null) : null;
  const today = toLocalDateStr();

  return (
    <div className="space-y-6">
      {/* Full spinner — only on first load when there is no cached data */}
      {hardLoading && (
        <div className="flex items-center justify-center min-h-[70vh]">
          <div className={loaderStyles.loader} />
        </div>
      )}

      {/* Error state — only shown when stable (not mid-transition).
          Gating on !isShowingPlaceholder prevents stale errors from a prior
          professional flashing while the new professional's data is loading. */}
      {!hardLoading && !isShowingPlaceholder && activeError && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Failed to load billing data.{" "}
          <button onClick={handleRetry} className="underline ml-1">
            Retry
          </button>
        </div>
      )}

      {/* Controls + data content load together — dimmed while showing a prior
          professional's placeholder data. */}
      {!hardLoading && (
        <div
          className={`space-y-6 transition-opacity duration-200${isShowingPlaceholder ? " opacity-60" : ""}`}
        >
          {/* Controls */}
          <YoYControls
            professionals={professionals}
            profId={profId}
            onProfIdChange={setProfId}
            granularity={granularity}
            onGranularityChange={setGranularity}
          />
          {/* KPI Cards */}
          {summary && (
            <YoYKpiCards summary={summary} granularity={granularity} />
          )}

          {/* Daily line chart — single attorney only */}
          {granularity === "daily" && profId && dailyData && (
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-sm font-semibold text-foreground">
                Daily Hours Moving Average
              </h3>
              {transitioning ? (
                <p className="text-xs text-muted-foreground mt-0.5 mb-4">Updating…</p>
              ) : goLiveDate ? (
                <p className="text-xs text-muted-foreground mt-0.5 mb-4">
                  Go-live: {formatGoLiveDate(goLiveDate)} · Comparing {computeDaysBetween(goLiveDate, today)} days
                </p>
              ) : (
                <div className="mb-4" />
              )}
              <DailyViewChart data={dailyData} />
            </div>
          )}

          {/* Weekly/monthly bar chart */}
          {granularity !== "daily" && timeSeriesData.length > 0 && (
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-sm font-semibold text-foreground">
                Hours Comparison — {granularity === "weekly" ? "Weekly" : "Monthly"}
              </h3>
              {transitioning ? (
                <p className="text-xs text-muted-foreground mt-0.5 mb-4">Updating…</p>
              ) : goLiveDate ? (
                <p className="text-xs text-muted-foreground mt-0.5 mb-4">
                  Go-live: {formatGoLiveDate(goLiveDate)} · Comparing {computeDaysBetween(goLiveDate, today)} days
                </p>
              ) : (
                <div className="mb-4" />
              )}
              <YoYChart
                data={timeSeriesData}
                granularity={granularity}
                selectedProfId={profId}
                goLiveDates={goLiveDateMap}
              />
            </div>
          )}
        </div>
      )}

      {/* Empty state — only when fully loaded with no data and no error */}
      {!hardLoading && !activeError && !summary && timeSeriesData.length === 0 && (
        <p className="text-center text-muted-foreground text-sm py-12">
          No billing data available. Data will appear here once billing
          transactions are synced and attorneys have Hourglass activity.
        </p>
      )}
    </div>
  );
}
